import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@tellescope/ui";
import { Button } from "@tellescope/design-system";
import { useCartStore } from "../cartStore";
import { formatShopPrice } from "../formatShopPrice";
import { getDefaultVariant } from "../shopVariants";
import { PriceBadge } from "../atoms/PriceBadge";
import { SecondaryProductBadge } from "../atoms/SecondaryProductBadge";
import { ProductImageCarousel } from "../molecules/ProductImageCarousel";
import type { ShopProduct } from "../types";

interface ProductDialogProps {
  /** The product to display, or null when the dialog is closed. */
  product: ShopProduct | null;
  secondaryBadge?: string;
  onClose: () => void;
}

/**
 * Learn more dialog. Mirrors ProductCard's styling (rounded card, carousel + badges, gradient
 * lower section) at a larger scale, with the full description in place of the "Learn more" link.
 */
export const ProductDialog = ({ product, secondaryBadge, onClose }: ProductDialogProps) => {
  const { t } = useTranslation("shop");
  const addToCart = useCartStore((state) => state.addToCart);
  const isInCart = useCartStore((state) =>
    product ? state.items.some((item) => item.stripeProductId === product.stripeProductId) : false
  );
  const defaultVariant = product ? getDefaultVariant(product) : null;
  const sortedVariants = useMemo(
    () => (product ? product.variants.slice().sort((a, b) => a.amount - b.amount) : []),
    [product]
  );

  return (
    <Dialog open={!!product} onOpenChange={(open) => !open && onClose()}>
      {product && defaultVariant && (
        <DialogContent
          data-test="product-dialog"
          overlayVariant="light"
          className="max-w-xl p-0 gap-0 overflow-hidden rounded-[24px]"
        >
          <DialogTitle className="sr-only">{t("{{title}} details", { title: product.title })}</DialogTitle>

          <div className="relative flex-shrink-0">
            <ProductImageCarousel name={product.title} images={product.image ? [product.image] : []} className="h-72" />
            <div className="absolute top-3 left-3 flex gap-1.5 z-10">
              {secondaryBadge && <SecondaryProductBadge label={secondaryBadge} className="text-xs px-2.5 py-1" />}
              <PriceBadge label={formatShopPrice(t, defaultVariant)} className="text-xs px-2.5 py-1" />
            </div>
          </div>

          <div
            className="p-7 flex flex-col gap-3"
            style={{
              background: "linear-gradient(135deg, hsl(var(--primary) / 0.1) 0%, hsl(var(--primary) / 0) 100%)",
            }}
          >
            <h3 className="font-semibold text-foreground text-2xl leading-tight">{product.title}</h3>
            {product.description && (
              <p className="text-sm text-muted-foreground leading-relaxed">{product.description}</p>
            )}
            {product.variants.length > 1 && (
              <div className="mt-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                  {t("Available prices")}
                </p>
                <ul data-test="dialog-available-prices" className="flex flex-col gap-1">
                  {sortedVariants.map((variant) => (
                    <li
                      key={variant.stripePriceId}
                      className="flex items-center justify-between text-sm text-foreground"
                    >
                      <span className="text-muted-foreground">
                        {variant.isSubscription ? t("Subscription") : t("One-time")}
                      </span>
                      <span className="font-medium">{formatShopPrice(t, variant)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-center justify-end mt-2 pt-2">
              <Button
                data-test="dialog-add-to-cart"
                onClick={() => {
                  if (isInCart) {
                    toast.info(t("{{title}} is already in your cart", { title: product.title }));
                  } else {
                    addToCart(product.stripeProductId, defaultVariant.stripePriceId);
                    toast.success(t("{{title}} added to cart", { title: product.title }));
                  }
                  onClose();
                }}
              >
                {t("Add to cart")}
              </Button>
            </div>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};
